import { useEffect, useState } from 'react';
import { Receipt, DollarSign, Clock, CheckCircle } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import StatsCard from '../../../components/common/StatsCard';
import EmptyState from '../../../components/common/EmptyState';
import StatusChip from '../components/shared/StatusChip';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { DataTable } from '../../../components/ui/data-table';
import { pammFeeService } from '../services/pammService';
import { formatCurrency, formatDate, capitalize, exportToCSV } from '../utils';
import type { FeeRecord } from '../types';
import { PAMM_FEE_TYPE_OPTIONS } from '../constants';
import type { DataTableColumn } from '../../../components/ui/data-table';

export default function FeesCommission() {
  const [fees, setFees] = useState<FeeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setFees(await pammFeeService.getAll());
    } catch {
      setError('Failed to load fees.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  const collected = fees.filter(f => f.status === 'collected');
  const pending = fees.filter(f => f.status === 'pending');
  const totalCollected = collected.reduce((s, f) => s + f.amount, 0);

  const columns: DataTableColumn<FeeRecord>[] = [
    {
      id: 'fund',
      header: 'Fund',
      accessor: row => (
        <span className="text-xs font-medium">{row.fundName}</span>
      ),
      sortable: true,
      sortValue: row => row.fundName,
    },
    {
      id: 'type',
      header: 'Fee Type',
      accessor: row => (
        <StatusChip
          status={row.type}
          label={capitalize(row.type) + ' Fee'}
          size="sm"
        />
      ),
    },
    {
      id: 'investor',
      header: 'Investor',
      accessor: row => (
        <span className="text-xs">{row.investorName ?? '—'}</span>
      ),
    },
    {
      id: 'period',
      header: 'Period',
      accessor: row => <span className="text-xs">{row.period}</span>,
    },
    {
      id: 'rate',
      header: 'Rate',
      accessor: row => <span className="text-xs">{row.feeRate}%</span>,
    },
    {
      id: 'base',
      header: 'Base Amount',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {row.baseAmount ? formatCurrency(row.baseAmount) : '—'}
        </span>
      ),
    },
    {
      id: 'amount',
      header: 'Fee Amount',
      accessor: row => (
        <span className="text-xs font-semibold">
          {formatCurrency(row.amount, row.currency)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.amount,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <StatusChip status={row.status} size="sm" />,
    },
    {
      id: 'date',
      header: 'Collected',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {row.collectedAt ? formatDate(row.collectedAt) : '—'}
        </span>
      ),
      sortable: true,
      sortValue: row => row.collectedAt,
    },
  ];

  if (loading)
    return (
      <div className="space-y-4">
        <PageHeader title="Fees & Commission" />
        <LoadingSkeleton type="table" />
      </div>
    );
  if (error)
    return (
      <div className="space-y-4">
        <PageHeader title="Fees & Commission" />
        <PAMMErrorState message={error} onRetry={load} />
      </div>
    );

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="Fees & Commission"
        description="Track management, performance, entry and exit fees across all funds"
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard title="Total Records" value={fees.length} icon={Receipt} />
        <StatsCard
          title="Collected"
          value={collected.length}
          icon={CheckCircle}
        />
        <StatsCard title="Pending" value={pending.length} icon={Clock} />
        <StatsCard
          title="Total Collected"
          value={formatCurrency(totalCollected, 'USD', true)}
          icon={DollarSign}
        />
      </div>
      {fees.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No Fee Records"
          description="Fee and commission records will appear here."
        />
      ) : (
        <DataTable
          columns={columns}
          data={fees}
          searchValue={searchValue}
          searchPlaceholder="Search fees..."
          showColumnVisibility={true}
          onSearchChange={setSearchValue}
          onSearchReset={() => setSearchValue('')}
          filterFields={[
            { id: 'type', label: 'Type', options: PAMM_FEE_TYPE_OPTIONS },
          ]}
          activeFilters={activeFilters}
          onFiltersChange={setActiveFilters}
          showExportButton={true}
          exportButtonText="Export CSV"
          onExportClick={() => exportToCSV(fees, 'pamm_fees')}
        />
      )}
    </div>
  );
}
